import { Stack } from "@mui/material";
import { ChangingParameters } from "../types";
import { OptionExpirationSelector } from "./OptionExpirationSelector";
import { OptionStrikeSelector } from "./OptionStrikeSelector";
import { OrderSizeSelector } from "./order-size-selector/OrderSizeSelector";
import { ExpandToggleButton } from "../views";
import { SavePortfolioButton } from "./SavePortfolioButton";
import { Dispatch, SetStateAction } from "react";
import { AssetType } from "../../../shared/types/AssetType";
import { TargetPlatform } from "../../../shared/types/TargetPlatform";
import { ToolsPanelFilterSelect } from "./ToolsPanelFilterSelect";
import { OrderState } from "../../../shared/types/OrderState";
import { OrderFilterAvailability } from "../utils/OrderFilterUtils";
import { AssetTypeFilterSelect } from "./AssetTypeFilterSelect";
import { FilterResetButton } from "./FilterResetButton";
import { TargetPlatformFilterSelect } from "./TargetPlatformFilterSelect";

type OrdersToolsPanelProps = {
    selectedOrderStateFilter: OrderState | null;
    setSelectedOrderStateFilter: Dispatch<SetStateAction<OrderState | null>>;
    selectedAssetTypeFilter: AssetType | null;
    setSelectedAssetTypeFilter: Dispatch<SetStateAction<AssetType | null>>;
    selectedTargetPlatformFilter: TargetPlatform | null;
    setSelectedTargetPlatformFilter: Dispatch<SetStateAction<TargetPlatform | null>>;
    filterAvailability: OrderFilterAvailability;
    resetFilters: () => void;
    isPortfolioChanged: boolean;
    setIsPortfolioChanged: React.Dispatch<React.SetStateAction<boolean>>;
    changingParameters: ChangingParameters;
    isExpanded: boolean;
    setIsExpanded: Dispatch<SetStateAction<boolean>>;
};

export function OrdersToolsPanel({
    selectedOrderStateFilter,
    setSelectedOrderStateFilter,
    selectedAssetTypeFilter,
    setSelectedAssetTypeFilter,
    selectedTargetPlatformFilter,
    setSelectedTargetPlatformFilter,
    filterAvailability,
    resetFilters,
    isPortfolioChanged,
    setIsPortfolioChanged,
    changingParameters,
    isExpanded,
    setIsExpanded,
}: OrdersToolsPanelProps) {
    const orderStateOptions = [
        { value: '', label: 'All states' },
        { value: 'Disabled', label: 'Disabled', disabled: !filterAvailability.orderStates.has(OrderState.Disabled) },
        { value: 'Active', label: 'Active', disabled: !filterAvailability.orderStates.has(OrderState.Active) },
        { value: 'Archived', label: 'Archived', disabled: !filterAvailability.orderStates.has(OrderState.Archived) },
    ];

    const handleOrderStateFilterChange = (value: string): void => {
        setSelectedOrderStateFilter(value === ''
            ? null
            : OrderState[value as keyof typeof OrderState]);
    };

    const isAnyFilterApplied =
        selectedTargetPlatformFilter !== null ||
        selectedAssetTypeFilter !== null ||
        selectedOrderStateFilter !== null;

    return(
        <Stack
            direction="row"
            spacing={1}
            alignItems="center"
        >
            <Stack direction="row" spacing={0.5}>
                <FilterResetButton
                    isAnyFilterApplied={isAnyFilterApplied}
                    resetFilters={resetFilters}
                />
                <ToolsPanelFilterSelect
                    id="ordersStateFilter"
                    value={selectedOrderStateFilter === null ? '' : OrderState[selectedOrderStateFilter]}
                    options={orderStateOptions}
                    isFilterApplied={selectedOrderStateFilter !== null}
                    fallbackLabel="All states"
                    onChange={handleOrderStateFilterChange}
                    width={100}
                />
                <TargetPlatformFilterSelect
                    id="ordersTargetPlatformFilter"
                    selectedTargetPlatformFilter={selectedTargetPlatformFilter}
                    setSelectedTargetPlatformFilter={setSelectedTargetPlatformFilter}
                    availableTargetPlatforms={filterAvailability.targetPlatforms}
                />
                <AssetTypeFilterSelect
                    id="ordersAssetTypeFilter"
                    selectedAssetTypeFilter={selectedAssetTypeFilter}
                    setSelectedAssetTypeFilter={setSelectedAssetTypeFilter}
                    availableAssetTypes={filterAvailability.assetTypes}
                />
            </Stack>
            <OptionExpirationSelector parameters={changingParameters} />
            <OptionStrikeSelector parameters={changingParameters} />
            <OrderSizeSelector parameters={changingParameters} />
            <SavePortfolioButton
                isPortfolioChanged={isPortfolioChanged}
                setIsPortfolioChanged={setIsPortfolioChanged}
            />
            <ExpandToggleButton
                isActivated={isExpanded}
                setIsActivated={setIsExpanded}
                tooltipTextWhenActivated="Restore widget"
                tooltipTextWhenDeactivated="Maximise widget"
            />
        </Stack>
    )
}
